import { useCallback, useState } from "react";
import { getAccessToken, refreshAccessToken } from "../utils/auth-token";

// Token-based http hook for the Welocate admin. Separate from use-http.js,
// which is tied to Auth0 sessions for the main app.
const useAdminHttp = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const sendRequest = useCallback(async (requestConfig, applyData) => {
    setIsLoading(true);
    setError(null);

    const executeFetch = () => {
      const token = getAccessToken();

      // File uploads leave the content type to the browser so it can set
      // the multipart boundary.
      const headers = {
        ...(requestConfig.isFileUpload === true
          ? {}
          : { "Content-type": "application/json; charset=utf-8" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...requestConfig.headers,
      };

      return fetch(requestConfig.url, {
        method: requestConfig.method ? requestConfig.method : "GET",
        headers: headers,
        credentials: "include",
        body: requestConfig.body
          ? requestConfig.isFileUpload === true
            ? requestConfig.body
            : JSON.stringify(requestConfig.body)
          : null,
      });
    };

    try {
      let response = await executeFetch();

      // Access token expired: refresh via the httpOnly cookie and retry once.
      // Requests that are themselves credential checks (login) opt out.
      if (response.status === 401 && requestConfig.authRetry !== false) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          response = await executeFetch();
        }
      }

      if (!response.ok) {
        // Callers can supply their own copy per status code, e.g. a login
        // step that wants "try again later" for a 502 instead of the raw detail.
        let message = requestConfig.errorMessages?.[response.status] || null;
        try {
          const responseData = await response.json();
          message = message || responseData.detail;
        } catch {
          // Error response without a JSON body; fall through to a default.
        }
        if (!message) {
          message =
            response.status === 401 || response.status === 403
              ? "Invalid credentials."
              : `Request failed (${response.status}).`;
        }
        throw new Error(message);
      }

      let data = null;

      if (response.status !== 204) {
        data = await response.json();
      }

      applyData(data);
    } catch (err) {
      // fetch itself rejects with a TypeError when the browser never got a
      // readable response (network down, CORS-blocked error response, …).
      setError(
        err instanceof TypeError
          ? "Cannot reach the server. Please try again."
          : err.message || "Something went wrong."
      );
    }
    setIsLoading(false);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    isLoading,
    error,
    clearError,
    sendRequest,
  };
};

export default useAdminHttp;
